import { useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { AdditionalDataState } from '../../../../reducers/additionalDataSlice';
import {
  ImageIntentContext,
  registerSaveShortcutContext,
} from '../../../../utils/imageIntentReporter';
import { IMAGE_INTENT_ATTRIBUTE } from './ImageIntentTouchStyle';
import { imageIntentGalleryFromAdditionalData } from './imageIntentGallery';
import {
  createImageIntentGestureTracker,
  ImageIntentGestureTracker,
  pointerTypeOf,
  touchPointOf,
} from './imageIntentGestures';

export interface FrictionProps {
  [IMAGE_INTENT_ATTRIBUTE]: string;
  onContextMenu: (event: React.MouseEvent) => void;
  onDragStart: (event: React.DragEvent) => void;
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchMove: (event: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
}

interface UseImageIntentFriction {
  getFrictionProps: (context?: ImageIntentContext) => FrictionProps;
}

type GalleryAttribution = Required<
  Pick<ImageIntentContext, 'galleryKey' | 'picsGalleryId' | 'dp2GalleryId'>
>;

interface StateWithAdditionalData {
  additionalData?: Partial<AdditionalDataState> | null;
}

// The gallery the parent entered this ordering flow with (one definition,
// shared with cart snapshots and the SweetAlert dialogs).
const selectGalleryDefaults = (
  state: StateWithAdditionalData,
): GalleryAttribution =>
  imageIntentGalleryFromAdditionalData(state.additionalData ?? null);

// The three gallery fields travel as a unit. A context that sets any of them
// (even to null, meaning "no gallery") knows its image's gallery, so its
// three are used as-is; otherwise the Redux gallery fills all three. Mixing
// the two could pair one gallery's key with another gallery's id.
const resolveGallery = (
  context: ImageIntentContext,
  defaults: GalleryAttribution,
): GalleryAttribution => {
  const explicit =
    context.galleryKey !== undefined ||
    context.picsGalleryId !== undefined ||
    context.dp2GalleryId !== undefined;
  if (!explicit) return defaults;

  return {
    galleryKey: context.galleryKey ?? null,
    picsGalleryId: context.picsGalleryId ?? null,
    dp2GalleryId: context.dp2GalleryId ?? null,
  };
};

// Save-intent handlers for preview images. Spread the returned props onto the
// image (or its container — touch/context events bubble). Every attempt is
// reported, and the gestures that save are blocked: right-click everywhere,
// finger drags, and — through the data attribute and ImageIntentTouchStyle —
// the iOS long-press save sheet (the last two only where the studio's save
// blocking is on). See createImageIntentGestureTracker.
//
// Reports carry the Redux gallery unless the context names its own (see
// resolveGallery). Pass `saveShortcutContext` to also report Cmd/Ctrl+S while
// the surface is mounted. The keystroke saves the page, not an image, so it is
// attributed to the surface rather than to one image; it is never suppressed.
const useImageIntentFriction = (
  saveShortcutContext?: ImageIntentContext,
): UseImageIntentFriction => {
  const trackerRef = useRef<ImageIntentGestureTracker>();
  if (!trackerRef.current) {
    trackerRef.current = createImageIntentGestureTracker();
  }
  const tracker = trackerRef.current;

  const galleryDefaults = useSelector(selectGalleryDefaults, shallowEqual);

  // A touch that starts just before the component unmounts (navigation, modal
  // close) must not fire a stale long-press report after the surface is gone.
  useEffect(() => () => tracker.dispose(), [tracker]);

  const saveShortcutSurface = saveShortcutContext?.surface;
  const saveShortcutGallery = resolveGallery(
    saveShortcutContext ?? {},
    galleryDefaults,
  );
  const { galleryKey, picsGalleryId, dp2GalleryId } = saveShortcutGallery;
  useEffect(() => {
    if (!saveShortcutSurface) return undefined;

    return registerSaveShortcutContext({
      surface: saveShortcutSurface,
      galleryKey,
      picsGalleryId,
      dp2GalleryId,
    });
  }, [saveShortcutSurface, galleryKey, picsGalleryId, dp2GalleryId]);

  const getFrictionProps = (
    context: ImageIntentContext = {},
  ): FrictionProps => {
    const reported: ImageIntentContext = {
      ...context,
      ...resolveGallery(context, galleryDefaults),
    };

    return {
      [IMAGE_INTENT_ATTRIBUTE]: '',
      // React 16's synthetic MouseEvent has no pointerType; the native event
      // (a PointerEvent in Chromium) says whether a finger opened the menu.
      onContextMenu: (event: React.MouseEvent) =>
        tracker.contextMenu(event, reported, pointerTypeOf(event.nativeEvent)),
      onDragStart: (event: React.DragEvent) =>
        tracker.dragStart(event, reported),
      onTouchStart: (event: React.TouchEvent) =>
        tracker.touchStart(reported, touchPointOf(event), event.touches.length),
      onTouchMove: (event: React.TouchEvent) =>
        tracker.touchMove(touchPointOf(event), event.touches.length),
      onTouchEnd: tracker.touchEnd,
      onTouchCancel: () => tracker.touchCancel(reported),
    };
  };

  return { getFrictionProps };
};

export default useImageIntentFriction;
